/**
 * OneTrust
 *
 * https://www.onetrust.com/
 */

export const onetrust = {

	/**
	 * Get the OneTrust cookie.
	 *
	 * @returns {{statistics: boolean, marketing: boolean, preferences: boolean, necessary: boolean}|null}
	 */
	getConsent: () => {

		// let consent = "isGpcEnabled=0&datestamp=Sun+Apr+07+2024+06%3A58%3A30+GMT%2B0200+(Central+European+Summer+Time)&version=202401.1.0&browserGpcFlag=0&isIABGlobal=false&hosts=&consentId=ddb816ea-097a-4590-a2dc-17120c6c412c&interactionCount=2&landingPath=NotLandingPage&groups=1%3A1%2C2%3A0%2C3%3A1%2C4%3A1&geolocation=CH%3BZH&AwaitingReconsent=false"
		let consent = pmw.getCookie("OptanonConsent")

		if (!consent) return null

		const fields = consentToJSON(consent)
		const groups = getGroups(fields)

		/**
		 * OneTrust writes the OptanonConsent cookie for its own bookkeeping too,
		 * for example to carry the geolocation and the GPC flag. Such a cookie has
		 * no groups key at all, and reading it as a decision used to throw on
		 * consentObject.groups.split.
		 */
		if (!groups) {
			pmw.console.log("OneTrust cookie detected, but it carries no consent groups. Ignoring it.")
			return null
		}

		/**
		 * A cookie written before the visitor answered the banner carries
		 * interactionCount=0 and holds OneTrust's defaults, not a decision.
		 * OneTrust marks the answered banner with the OptanonAlertBoxClosed cookie,
		 * which its own JS API reads back through OneTrust.IsAlertBoxClosed(), and
		 * it counts every interaction in interactionCount.
		 *
		 * The guard stays deliberately narrow: only a cookie that states an
		 * interaction count of zero AND has no alert box cookie next to it is
		 * ignored. A cookie without an interaction count is read as before, because
		 * discarding it would fall back to PMW's own defaults, which in implicit
		 * consent mode grant more than the cookie itself says.
		 */
		if (bannerIsUnanswered(fields)) {
			pmw.console.log("OneTrust cookie detected, but the visitor hasn't answered the banner yet. Ignoring it.")
			return null
		}

		pmw.console.log("OneTrust CMP consent detected")

		// group mapping
		// 1 / C0001 = necessary
		// 2 / C0002 = analytics
		// 3 / C0003 = functional
		// 4 / C0004 = ads
		//
		// A group the cookie does not name is denied. Granting an unknown is what
		// the deny by default policy forbids. Same change as the iubenda reader in
		// 1.58.10.
		return {
			statistics : groupValue(groups, "2") ?? false,
			marketing  : groupValue(groups, "4") ?? false,
			preferences: groupValue(groups, "3") ?? false,
			necessary  : groupValue(groups, "1") ?? true,
		}
	},

	/**
	 * OneTrust Event Listeners
	 */
	loadEventListeners: () => {

		document.addEventListener("consent.onetrust", (e) => {

			const active = activeGroups(e.detail)

			let data = {
				statistics : isActive(active, "2"),
				marketing  : isActive(active, "4"),
				preferences: isActive(active, "3"),
				// Necessary is granted unless something denies it through our own
				// API, the same as in the cookie reader above.
				necessary  : true,
			}

			pmw.consent.api.updateSelectively({...data, source: "cmp"})
		}, true)
	},
}

/**
 * Read one category, by its bare number and by the C000n id OneTrust uses for
 * its own default categories.
 *
 * @param groups {Object}
 * @param id {string}
 * @returns {boolean|undefined}
 */
const groupValue = (groups, id) => groups[id] ?? groups["C000" + id]

/**
 * The group ids an OnetrustActiveGroups style event carries, as a list.
 *
 * OneTrust hands them over either as an array or as a comma separated string,
 * and the string form is padded with commas ",C0001,C0003,".
 *
 * @param detail {string[]|string}
 * @returns {string[]}
 */
const activeGroups = (detail) => {

	if (Array.isArray(detail)) return detail.map(String)
	if (typeof detail === "string") return detail.split(",")

	return []
}

/**
 * Whether a category was granted, by its bare number and by the C000n id.
 *
 * The ids have to be compared whole. A substring match reads "C0002" as a grant
 * for group 2 on a site that denied it, because the character is in there.
 *
 * @param active {string[]}
 * @param id {string}
 * @returns {boolean}
 */
const isActive = (active, id) => active.some(group => {
	group = String(group).trim()
	return group === id || group === "C000" + id
})

/**
 * Whether the cookie was written before the visitor answered the banner.
 *
 * @param fields {Object} - the raw key value pairs of the cookie
 * @returns {boolean}
 */
const bannerIsUnanswered = (fields) => {

	if (fields.interactionCount === undefined) return false
	if (Number(fields.interactionCount) !== 0) return false

	// OneTrust sets this once the visitor has interacted with the banner.
	return !pmw.getCookie("OptanonAlertBoxClosed")
}

/**
 * Turn the OptanonConsent cookie into its raw key value pairs.
 *
 * @param consent {string}
 * @returns {Object}
 */
const consentToJSON = (consent) => {

	try {
		// pmw.getCookie() already decodes the cookie once. A OneTrust cookie whose
		// groups are still percent encoded needs the second pass, and a stray
		// percent sign in an already decoded cookie must not throw here: the
		// readers run one after another, so an error in one of them would leave
		// the whole consent state unresolved.
		consent = decodeURIComponent(consent)
	} catch (e) {
		// keep the cookie as it is
	}

	return consent.split("&").reduce((obj, item) => {
		let [key, value] = item.split("=")
		obj[key]         = value
		return obj
	}, {})
}

/**
 * Turn the groups field into a category to boolean map.
 *
 * @param fields {Object}
 * @returns {Object|null} - null when the cookie names no groups
 */
const getGroups = (fields) => {

	if (!fields.groups) return null

	const groups = fields.groups.split(",").reduce((obj, item) => {
		let [key, value] = item.split(":")
		if (key) obj[key] = value === "1"
		return obj
	}, {})

	return Object.keys(groups).length > 0 ? groups : null
}
